/**
 * Reporting / analytics queries.
 *
 * The Reporting page used to call `supabase.from(...)` directly for ~12
 * different aggregations. ESLint forbids that pattern (ADR-0002) because
 * it scatters database access throughout the UI and makes it impossible
 * to swap the storage layer or apply consistent error handling. Each
 * helper here corresponds to exactly one chart / KPI on the Reporting
 * page, so the React Query call site stays a one-liner.
 *
 * Type policy: the row shapes returned here are local projections, not
 * the deeply-nested generated table types. Reporting only needs a
 * handful of columns per query and modelling them inline (a) keeps this
 * file readable, and (b) avoids dragging the entire `Database` generic
 * into the Recharts code paths.
 *
 * Runtime-Typisierung: Die Supabase-Typgenerierung ist hier nicht immer
 * deckungsgleich mit dem, was PostgREST tatsaechlich liefert (Joins,
 * Views, COALESCE etc.). Wir haben daher Zod-Schemas pro Row-Shape und
 * validieren die Antwort vor der Rueckgabe. Wenn eine einzelne Zeile
 * nicht matcht, wird sie verworfen und die Fehlerzahl per console.warn
 * gemeldet – der Dashboard-Flow laeuft weiter mit den valid parsbaren
 * Zeilen. Das ersetzt die frueheren `as unknown as X[]`-Casts
 * (Finding #29 aus docs/audit-2026-04-22.md).
 */

import { z } from 'zod';
import { supabase } from '@/integrations/supabase/client';

// ─── Row schemas + derived types ───────────────────────────────────────────

const NrsScreeningSchema = z.object({
  screening_date: z.string(),
  total_score: z.number(),
  risk_level: z.string(),
  patient_id: z.string(),
});
export type NrsScreeningRow = z.infer<typeof NrsScreeningSchema>;

const MnaScreeningSchema = z.object({
  screening_date: z.string(),
  total_score: z.number(),
  risk_category: z.string(),
  patient_id: z.string(),
});
export type MnaScreeningRow = z.infer<typeof MnaScreeningSchema>;

const LabValueSchema = z.object({
  measured_at: z.string(),
  albumin_g_dl: z.number().nullable(),
  crp_mg_l: z.number().nullable(),
  prealbumin_mg_dl: z.number().nullable(),
});
export type LabValueRow = z.infer<typeof LabValueSchema>;

const AnthropometryBmiSchema = z.object({
  patient_id: z.string(),
  // `numeric` aus Postgres kommt je nach Treiber als number oder string an –
  // Recharts/BMI-Rechnungen parsen beides, also erlauben wir beides.
  bmi: z.union([z.number(), z.string()]).nullable(),
  measured_at: z.string(),
});
export type AnthropometryBmiRow = z.infer<typeof AnthropometryBmiSchema>;

const NrsCourseSchema = z.object({
  screening_date: z.string(),
  total_score: z.number(),
});
export type NrsCourseRow = z.infer<typeof NrsCourseSchema>;

const AnthropometryWeightSchema = z.object({
  measured_at: z.string(),
  weight_kg: z.number(),
});
export type AnthropometryWeightRow = z.infer<typeof AnthropometryWeightSchema>;

const ReportingInvoiceSchema = z.object({
  id: z.string(),
  invoice_date: z.string(),
  total_amount: z.number().nullable(),
  status: z.string(),
  billing_type: z.string().nullable(),
  created_at: z.string(),
});
export type ReportingInvoiceRow = z.infer<typeof ReportingInvoiceSchema>;

const StatusRowSchema = z.object({ status: z.string() });
export type StatusRow = z.infer<typeof StatusRowSchema>;

// ─── Validation helper ──────────────────────────────────────────────────────

/**
 * Validiert eine (vermeintliche) Liste von Rows gegen das Schema. Invalid
 * parsbare Eintraege werden verworfen; die Anzahl wird einmalig pro Call
 * geloggt (nicht pro Zeile), damit wir die Zahl im Dev-Tool sehen, ohne
 * die Konsole zu fluten.
 */
function parseRows<T>(
  schema: z.ZodType<T>,
  data: unknown,
  source: string,
): T[] {
  if (!Array.isArray(data)) return [];
  const result: T[] = [];
  let dropped = 0;
  for (const row of data) {
    const parsed = schema.safeParse(row);
    if (parsed.success) result.push(parsed.data);
    else dropped += 1;
  }
  if (dropped > 0) {
    // Kein PHI im Log – nur Zahl + Quelle.
    console.warn(`[reporting] ${source}: ${dropped} Zeilen verworfen (Schema mismatch)`);
  }
  return result;
}

/**
 * Fehler einer Auswertungsabfrage — mit Angabe, welche.
 *
 * Warum das hier steht: Alle zwölf Helfer haben ihren `error` verworfen
 * und `0` bzw. `[]` zurückgegeben. Auf der Auswertungsseite wird daraus
 * eine Null in der Kennzahl oder eine leere Verlaufskurve —
 * ununterscheidbar von „in diesem Zeitraum gab es keine Screenings".
 * Diese Zahlen wandern in die Nachweisführung (§110a SGB V, KHZG FTB-4):
 * aus „unbekannt" wird dort eine belastbar wirkende 0, und eine
 * Einrichtung weist damit versehentlich aus, sie habe nicht gescreent.
 *
 * Der Name der Quelle steht im Fehler, damit die Oberfläche sagen kann,
 * WAS fehlt, statt pauschal „Fehler".
 */
export class ReportingQueryError extends Error {
  constructor(
    readonly source: string,
    readonly reason: string,
  ) {
    super(`[reporting] ${source}: ${reason}`);
    this.name = 'ReportingQueryError';
  }
}

// ─── Helpers ────────────────────────────────────────────────────────────────

/** Count of non-deleted patients in an organisation. */
export async function countActivePatients(orgId: string): Promise<number> {
  const { count, error } = await supabase
    .from('patients')
    .select('*', { count: 'exact', head: true })
    .eq('organization_id', orgId)
    .eq('is_deleted', false);
  if (error) throw new ReportingQueryError('countActivePatients', error.message);
  return count ?? 0;
}

/** Distinct patients that have at least one GNCP entry since `sinceIso`. */
export async function countActiveGncpPatients(orgId: string, sinceIso: string): Promise<number> {
  const { data, error } = await supabase
    .from('gncp_entries')
    .select('patient_id')
    .eq('organization_id', orgId)
    .is('deleted_at', null)
    .gte('created_at', sinceIso);
  if (error) throw new ReportingQueryError('countActiveGncpPatients', error.message);
  const unique = new Set((data ?? []).map((d) => d.patient_id));
  return unique.size;
}

/** NRS screenings since the given ISO date (year-month-day). */
export async function listNrsScreeningsSince(
  orgId: string,
  sinceDate: string,
): Promise<NrsScreeningRow[]> {
  const { data, error } = await supabase
    .from('clinical_nrs_screenings')
    .select('screening_date, total_score, risk_level, patient_id')
    .eq('organization_id', orgId)
    .gte('screening_date', sinceDate)
    .order('screening_date');
  if (error) throw new ReportingQueryError('listNrsScreeningsSince', error.message);
  return parseRows(NrsScreeningSchema, data, 'listNrsScreeningsSince');
}

/** MNA screenings since the given ISO date. */
export async function listMnaScreeningsSince(
  orgId: string,
  sinceDate: string,
): Promise<MnaScreeningRow[]> {
  const { data, error } = await supabase
    .from('clinical_mna_screenings')
    .select('screening_date, total_score, risk_category, patient_id')
    .eq('organization_id', orgId)
    .gte('screening_date', sinceDate)
    .order('screening_date');
  if (error) throw new ReportingQueryError('listMnaScreeningsSince', error.message);
  return parseRows(MnaScreeningSchema, data, 'listMnaScreeningsSince');
}

/** Lab values (albumin, CRP, prealbumin) since the given date. */
export async function listLabValuesSince(
  orgId: string,
  sinceDate: string,
): Promise<LabValueRow[]> {
  const { data, error } = await supabase
    .from('lab_values')
    .select('measured_at, albumin_g_dl, crp_mg_l, prealbumin_mg_dl')
    .eq('organization_id', orgId)
    .is('deleted_at', null)
    .gte('measured_at', sinceDate)
    .order('measured_at');
  if (error) throw new ReportingQueryError('listLabValuesSince', error.message);
  return parseRows(LabValueSchema, data, 'listLabValuesSince');
}

/** All anthropometry rows that carry a BMI value (newest first). */
export async function listAnthropometryBmi(orgId: string): Promise<AnthropometryBmiRow[]> {
  const { data, error } = await supabase
    .from('anthropometry')
    .select('patient_id, bmi, measured_at')
    .eq('organization_id', orgId)
    .is('deleted_at', null)
    .not('bmi', 'is', null)
    .order('measured_at', { ascending: false });
  if (error) throw new ReportingQueryError('listAnthropometryBmi', error.message);
  return parseRows(AnthropometryBmiSchema, data, 'listAnthropometryBmi');
}

/** All anthropometry rows with BMI (oldest first) — used for first-vs-last comparison. */
export async function listAnthropometryBmiAsc(orgId: string): Promise<AnthropometryBmiRow[]> {
  const { data, error } = await supabase
    .from('anthropometry')
    .select('patient_id, bmi, measured_at')
    .eq('organization_id', orgId)
    .is('deleted_at', null)
    .not('bmi', 'is', null)
    .order('measured_at', { ascending: true });
  if (error) throw new ReportingQueryError('listAnthropometryBmiAsc', error.message);
  return parseRows(AnthropometryBmiSchema, data, 'listAnthropometryBmiAsc');
}

/** Full NRS history for the org (used by outcome-tracking trend chart). */
export async function listAllNrsScreenings(orgId: string): Promise<NrsCourseRow[]> {
  const { data, error } = await supabase
    .from('clinical_nrs_screenings')
    .select('screening_date, total_score')
    .eq('organization_id', orgId)
    .order('screening_date');
  if (error) throw new ReportingQueryError('listAllNrsScreenings', error.message);
  return parseRows(NrsCourseSchema, data, 'listAllNrsScreenings');
}

/** Status counts for GNCP entries (used to derive the "final" rate KPI). */
export async function listGncpStatuses(orgId: string): Promise<StatusRow[]> {
  const { data, error } = await supabase
    .from('gncp_entries')
    .select('status')
    .eq('organization_id', orgId)
    .is('deleted_at', null);
  if (error) throw new ReportingQueryError('listGncpStatuses', error.message);
  return parseRows(StatusRowSchema, data, 'listGncpStatuses');
}

/** Status counts for Heilmittelverordnungen (HMV completion KPI). */
export async function listHmvStatuses(orgId: string): Promise<StatusRow[]> {
  const { data, error } = await supabase
    .from('heilmittelverordnungen')
    .select('status')
    .eq('organization_id', orgId);
  if (error) throw new ReportingQueryError('listHmvStatuses', error.message);
  return parseRows(StatusRowSchema, data, 'listHmvStatuses');
}

/** Weight measurements since the given date (used for weekly trend). */
export async function listWeightTrendSince(
  orgId: string,
  sinceDate: string,
): Promise<AnthropometryWeightRow[]> {
  const { data, error } = await supabase
    .from('anthropometry')
    .select('measured_at, weight_kg')
    .eq('organization_id', orgId)
    .is('deleted_at', null)
    .not('weight_kg', 'is', null)
    .gte('measured_at', sinceDate)
    .order('measured_at');
  if (error) throw new ReportingQueryError('listWeightTrendSince', error.message);
  return parseRows(AnthropometryWeightSchema, data, 'listWeightTrendSince');
}

/** Invoice rows since the given date (used for the finance dashboard). */
export async function listInvoicesSince(
  orgId: string,
  sinceDate: string,
): Promise<ReportingInvoiceRow[]> {
  const { data, error } = await supabase
    .from('invoices')
    .select('id, invoice_date, total_amount, status, billing_type, created_at')
    .eq('organization_id', orgId)
    .gte('invoice_date', sinceDate)
    .order('invoice_date');
  if (error) throw new ReportingQueryError('listInvoicesSince', error.message);
  return parseRows(ReportingInvoiceSchema, data, 'listInvoicesSince');
}
